import { Flex } from "@chakra-ui/react";
import GamelightButton from "components/button";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "helper/GlobalLoadingDispatches";
import { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ComplexTable from "./components/ComplexTable";
import { getNotifications } from "./services";
import usePartnerNotificationStore from "./store";
import { columnsDataComplex } from "./variables/columnsData";
import PartnerNotificationPagination from "./components/Pagination";
import { useUserPermissions } from "hooks/useUserPermissions";

const NotificationManager: React.FC = () => {
  const { isNotificationManagerEnabled } = useUserPermissions();
  const store = usePartnerNotificationStore();
  const location = useLocation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isInit, setIsInit] = useState(false);

  const fetchNotifications = useCallback(
    async (page: number) => {
      if (isLoading) return;
      setIsLoading(true);
      showGlobalLoading();
      try {
        const response = await getNotifications({
          page,
          limit: 10,
        });
        const hasNext = response.length === 10;
        store.addNotifications(response, page, hasNext);
      } catch (error) {
        console.log("Failed to fetch notifications:", error);
      } finally {
        setIsLoading(false);
        hideGlobalLoading();
        setIsInit(true);
      }
    },
    [isLoading]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const currentPageData = store.getPageData(currentPage);
      if (page > currentPage && currentPageData.length === 0) {
        return;
      }
      history.push(`/notification-manager?page=${page}`);
      if (!store.paginatedData[page]) {
        fetchNotifications(page);
      }
    },
    [history, currentPage, store, fetchNotifications]
  );

  useEffect(() => {
    if (!location.search) {
      history.replace("/notification-manager?page=1");
      return;
    }

    const params = new URLSearchParams(location.search);
    const pageParam = Number(params.get("page")) || 1;
    setCurrentPage(pageParam);

    if (!isInit || store.shouldRefresh) {
      fetchNotifications(pageParam);
      if (store.shouldRefresh) {
        store.setShouldRefresh(false);
      }
    }
  }, [location.search, fetchNotifications, store.shouldRefresh, isInit]);

  const notifications = store.getPageData(currentPage);
  const hasNextPage = store.hasNextPageForPage(currentPage);

  if (!isNotificationManagerEnabled) {
    return <></>;
  }

  return (
    <Flex
      gap={4}
      direction="column"
      pt={{ base: "130px", md: "80px", xl: "80px" }}
    >
      <Flex justifyContent="flex-end">
        <GamelightButton
          onClick={() => history.push("/notification-manager/create")}
        >
          {"Create Notification"}
        </GamelightButton>
      </Flex>

      <ComplexTable
        isLoading={isLoading}
        columnsData={columnsDataComplex}
        tableData={notifications}
      />
      <PartnerNotificationPagination
        currentPage={currentPage}
        onPageChange={handlePageChange}
        hasNextPage={hasNextPage}
      />
    </Flex>
  );
};

export default NotificationManager;
